<template>
  <base-form :title="$t('invoice.edit.title')"
             :loading="loading"
             @submit="updateInvoice"
  >
    <template v-slot:header-left>
      <status-badge class="ml-3" :status="invoice.status"/>
      <div class="ml-3">
        <span class="font-medium mr-1">{{parseFloat(invoice.price_in) | toCurrency}}</span>
        <small>{{$t('generic.inclVAT')}}</small>
      </div>
    </template>
    <template v-slot:header-right>
      <div class="flex items-center">
        <base-button v-if="invoice.status === invoiceStatuses.DRAFT"
                     outline
                     size="sm"
                     @click="updateInvoiceStatus(invoiceStatuses.SENT)">
          {{$t('invoice.edit.sendInvoice')}}
        </base-button>
        <base-button v-if="invoice.status === invoiceStatuses.SENT"
                     outline
                     size="sm"
                     @click="updateInvoiceStatus(invoiceStatuses.PAID)">
          {{$t('invoice.edit.invoicePaid')}}
        </base-button>
        <base-button v-if="invoice.status === invoiceStatuses.DRAFT"
                     outline
                     type="danger"
                     size="sm"
                     class="ml-2"
                     @click="removeInvoice">
          {{$t('invoice.edit.removeInvoice')}}
        </base-button>
        <base-button type="default"
                     outline
                     size="sm"
                     class="ml-2"
                     @click="$router.go(-1)">
          {{$t('generic.back')}}
        </base-button>
      </div>
    </template>
    <section-separator has-border size="lg">
      {{$t('invoice.edit.clientDetails')}}
    </section-separator>

    <fg-input :label="$t('invoice.edit.client')"
              required
              :error="getError($t('invoice.edit.client'))">
      <client-autocomplete :client="model.client"
                           @change="onClientChange"
                           :search-params="clientSearchParams"
                           :name="$t('invoice.edit.client')"
                           v-validate="modelValidations.client">
      </client-autocomplete>
    </fg-input>

    <fg-input
      required
      :label="$t('invoice.edit.client_name')"
      :name="$t('invoice.edit.client_name')"
      :error="getError($t('invoice.edit.client_name'))"
      v-model="model.client_name"
      v-validate="modelValidations.client_name">
    </fg-input>

    <fg-input
      required
      :label="$t('invoice.edit.attention')"
      :name="$t('invoice.edit.attention')"
      :error="getError($t('invoice.edit.attention'))"
      v-model="model.client_attention"
      v-validate="'required'"
    >
    </fg-input>

    <fg-input
      :label="$t('invoice.edit.client_address')"
      :name="$t('invoice.edit.client_address')"
      v-model="model.client_address">
    </fg-input>


    <div class="row">
      <div class="col-md-4">
        <fg-input :label="$t('invoice.edit.client_country_code')"
                  :error="getError($t('invoice.edit.client_country_code'))">
          <country-autocomplete v-model="model.client_country_code"
                                :name="$t('invoice.edit.client_country_code')">
          </country-autocomplete>
        </fg-input>
      </div>
      <div class="col-md-4">
        <fg-input
          required
          :label="$t('invoice.edit.client_city')"
          :name="$t('invoice.edit.client_city')"
          :error="getError($t('invoice.edit.client_city'))"
          v-model="model.client_city"
          v-validate="modelValidations.client_city">
        </fg-input>
      </div>
      <div class="col-md-4">
        <fg-input
          required
          :label="$t('invoice.edit.client_postal_code')"
          :name="$t('invoice.edit.client_postal_code')"
          :error="getError($t('invoice.edit.client_postal_code'))"
          v-model="model.client_postal_code"
          v-validate="modelValidations.client_postal_code">
        </fg-input>
      </div>
    </div>

    <fg-input
      required
      :label="$t('invoice.edit.email')"
      :name="$t('invoice.edit.email')"
      :error="getError($t('invoice.edit.email'))"
      v-model="model.email"
      v-validate="modelValidations.email">
    </fg-input>

    <section-separator has-border size="lg">
      {{$t('invoice.edit.invoiceDetails')}}
    </section-separator>

    <fg-input
      :label="$t('invoice.edit.vat_number')"
      :name="$t('invoice.edit.vat_number')"
      :error="getError($t('invoice.edit.vat_number'))"
      v-model="model.vat_number"
      v-validate="modelValidations.vat_number">
    </fg-input>

    <section-separator has-border size="lg">
      <div class="flex justify-between">
        <span>{{$t('invoice.menu.invoiceLines')}}</span>
        <div>
          <span class="font-medium mr-1">{{parseFloat(invoice.price_in) | toCurrency}}</span>
          <small>{{$t('generic.inclVAT')}}</small>
        </div>
      </div>
    </section-separator>

    <data-table :entity="entity"
                :columns="columns"
                :data="invoice.invoice_lines"
                :service="service"
                :local-data="true"
                :searchable="false"
                :go-to-details-on-row-click="false"
                :go-to-add-on-click="false"
                :disable-permission-checks="true"
                :show-pagination="false"
                :operations="tableOperations"
                @on-add="addInvoiceLine"
                @on-delete="$emit('refresh')"
                ref="table">
      <template slot="name" slot-scope="{row}">
        {{row.name}}
      </template>

      <template slot="price_ex" slot-scope="{row}">
        {{parseFloat(row.price_ex) | toCurrency}}
      </template>
      <template slot="price_in" slot-scope="{row}">
        {{parseFloat(row.price_in) | toCurrency}}
      </template>
    </data-table>

    <invoice-line-modal :show.sync="showCreateModal"
                        :client="model.client"
                        :loading="loading"
                        @save="createInvoiceLine">
    </invoice-line-modal>
  </base-form>
</template>
<script>
  import get from 'lodash/get'
  import ClientAutocomplete from 'src/components/Autocomplete/ClientAutocomplete'
  import CountryAutocomplete from 'src/components/Autocomplete/CountryAutocomplete'
  import InvoiceLineModal from './InvoiceLineModal'
  import InvoiceService from 'src/services/InvoiceService'
  import InvoiceLinesService from 'src/services/InvoiceLinesService'
  import entities from 'src/config/entities'
  import invoiceStatuses from 'src/config/invoiceStatuses'

  export default {
    name: 'invoice-edit-card',
    components: {
      ClientAutocomplete,
      CountryAutocomplete,
      InvoiceLineModal
    },
    props: {
      invoice: {
        type: Object,
        default: () => ({})
      }
    },
    data () {
      return {
        entity: entities.INVOICE_LINE,
        service: InvoiceLinesService,
        invoiceStatuses,
        showCreateModal: false,
        loading: false,
        model: {},
        clientSearchParams: {
          with: 'addresses'
        },
        modelValidations: {
          client: {
            required: true
          },
          client_name: {
            required: true
          },
          client_address: {
            required: true
          },
          city: {
            required: true
          },
          country: {
            required: true
          },
          email: {
            required: true,
            email: true
          },
          po_number: {},
          vat_number: {}
        }

      }
    },
    computed: {
      columns () {
        return [
          {
            prop: 'name',
            label: this.$t('invoice.edit.lines.name'),
            minWidth: '115px',
            sortable: true
          },
          {
            prop: 'price_ex',
            label: this.$t('invoice.edit.lines.priceEx'),
            minWidth: '190px',
            sortable: true
          },
          {
            prop: 'price_in',
            label: this.$t('invoice.edit.lines.priceIn'),
            minWidth: '190px',
            sortable: true
          }
        ]
      },
      tableOperations () {
        return this.invoice.status === 'draft' ? 'add,delete' : ''
      },
    },
    methods: {
      getError (name) {
        return this.errors.first(name)
      },
      onClientChange (client) {
        this.model.client = client
        this.$emit('client-change', client)
        this.model.client_name = this.model.client_name || client.name
        this.model.vat_number = this.model.vat_number || client.vat_number
        this.model.email = this.model.email || client.email
        let primaryAddress = client.addresses.find(a => a.primary)
        if (primaryAddress) {
          this.model.client_postal_code = primaryAddress.postal_code
          this.model.client_country_code = primaryAddress.country_code
          this.model.client_city = primaryAddress.city
          this.model.client_address = primaryAddress.address_line_1
        }
      },
      addInvoiceLine () {
        this.showCreateModal = true
      },
      async createInvoiceLine (invoiceLine) {
        try {
          this.loading = true
          let {data} = await InvoiceLinesService.create(invoiceLine)
          this.model.invoice_lines.push(data)
          this.showCreateModal = false
          this.$emit('refresh')
        } finally {
          this.loading = false
        }
      },
      async updateInvoice () {
        let res = await this.$validator.validateAll()
        if (!res) {
          return
        }
        try {
          this.loading = true
          let data = {
            client_uuid: this.model.client.uuid,
            client_name: this.model.client_name,
            client_attention: this.model.client_attention,
            client_address: this.model.client_address,
            client_postal_code: this.model.client_postal_code,
            client_city: this.model.client_city,
            client_country_code: this.model.client_country_code,
            email: this.model.email,
            po_number: this.model.po_number,
            vat_number: this.model.vat_number,
          }
          await InvoiceService.update(this.model.uuid, data)
        } finally {
          this.loading = false
        }

      },
      async updateInvoiceStatus (status) {
        let oldStatus = this.invoice.status
        this.invoice.status = status
        try {
          await InvoiceService.updateInvoiceStatus(this.invoice.uuid, status)
          this.loading = true
        } catch (e) {
          this.invoice.status = oldStatus
        } finally {
          this.loading = false
        }
      },
      async removeInvoice () {
        await InvoiceService.delete(this.invoice.uuid)
        this.$router.push({name: 'invoice_list'})
      }
    },
    created () {
      this.$watch('invoice', newVal => {
        this.model = {
          ...this.model,
          ...newVal,
        }
      }, {immediate: true})
    }
  }
</script>
<style>
</style>
